import { forwardRef, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import './ui.css';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
  labelAction?: ReactNode;
  showPasswordToggle?: boolean;
}

function PasswordVisibilityIcon({ visible }: { visible: boolean }) {
  if (visible) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    id,
    className = '',
    labelAction,
    showPasswordToggle = false,
    type,
    ...props
  },
  ref
) {
  const inputId = id ?? label.toLowerCase().replace(/\s+/g, '-');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const isPasswordField = type === 'password' && showPasswordToggle;
  const inputType = isPasswordField && passwordVisible ? 'text' : type;

  const input = (
    <input
      ref={ref}
      id={inputId}
      type={inputType}
      className={`ui-input ${error ? 'ui-input--error' : ''} ${isPasswordField ? 'ui-input--with-toggle' : ''}`}
      {...props}
      aria-invalid={error ? true : props['aria-invalid']}
      aria-describedby={[props['aria-describedby'], error || hint ? `${inputId}-description` : null].filter(Boolean).join(' ') || undefined}
    />
  );

  return (
    <div className={`ui-field ${className}`.trim()}>
      <div className="ui-field__label-row">
        <label className="ui-field__label" htmlFor={inputId}>
          {label}
        </label>
        {labelAction}
      </div>
      {isPasswordField ? (
        <span className="ui-input-wrap">
          {input}
          <button
            type="button"
            className="ui-input__toggle"
            aria-label={passwordVisible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            aria-pressed={passwordVisible}
            disabled={props.disabled}
            onClick={() => setPasswordVisible((visible) => !visible)}
          >
            <PasswordVisibilityIcon visible={passwordVisible} />
          </button>
        </span>
      ) : (
        input
      )}
      {error ? <span id={`${inputId}-description`} className="ui-field__error">{error}</span> : hint ? <span id={`${inputId}-description`} className="ui-field__hint">{hint}</span> : null}
    </div>
  );
});

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: { value: string; label: string }[];
}

export function Select({ label, options, id, className = '', ...props }: SelectProps) {
  const selectId = id ?? label.toLowerCase().replace(/\s+/g, '-');
  return (
    <div className={`ui-field ${className}`.trim()}>
      <label className="ui-field__label" htmlFor={selectId}>
        {label}
      </label>
      <select id={selectId} className="ui-input" {...props}>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}
